import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import {
  ArrowRight,
  Calendar,
  Clock,
  MapPin,
  Star,
  MessageSquare,
  Minus,
  Plus,
  User as UserIcon,
  Shield,
  Package,
  LifeBuoy,
  Loader,
  ShoppingBag,
  Trash2,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_API_URL;

const UserDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const cartStorageKey = useMemo(() => `wearoclub_cart_${user?.id || 'guest'}`, [user?.id]);
  const defaultTab = useMemo(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    const allowedTabs = new Set(['profile', 'credentials', 'bookings', 'orders', 'support', 'cart']);
    return allowedTabs.has(tab) ? tab : 'profile';
  }, [location.search]);
  const [activeTab, setActiveTab] = useState(defaultTab);

  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportReplies, setSupportReplies] = useState({});
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [reviewData, setReviewData] = useState({ rating: 5, comment: '' });
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [isOpeningCartPayment, setIsOpeningCartPayment] = useState(false);
  const [payingItemKey, setPayingItemKey] = useState('');
  const [shippingDialogOpen, setShippingDialogOpen] = useState(false);
  const [shippingData, setShippingData] = useState({
    address: '',
    phone: '',
    notes: '',
  });
  const hasFetchedDashboardRef = useRef(false);

  useEffect(() => {
    setProfileData({
      name: user?.name || '',
      email: user?.email || '',
    });
  }, [user]);

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  useEffect(() => {
    if (!user?.id || hasFetchedDashboardRef.current) {
      return;
    }
    hasFetchedDashboardRef.current = true;
    fetchDashboardData();
  }, [user?.id]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(cartStorageKey);
      setCartItems(raw ? JSON.parse(raw) : []);
    } catch (error) {
      console.error('Error reading cart items:', error);
      setCartItems([]);
    }
  }, [cartStorageKey]);

  useEffect(() => {
    if (!shippingDialogOpen) {
      return;
    }
    setShippingData((current) => ({
      ...current,
      phone: current.phone || user?.phone || '',
    }));
  }, [shippingDialogOpen, user?.phone]);

  const fetchDashboardData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      navigate('/auth');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [bookingsRes, ordersRes, supportRes] = await Promise.allSettled([
        axios.get(`${API_URL}/bookings`, { headers }),
        axios.get(`${API_URL}/orders`, { headers }),
        axios.get(`${API_URL}/support-tickets/me`, { headers }),
      ]);

      if (bookingsRes.status === 'fulfilled') {
        setBookings(bookingsRes.value.data || []);
      }

      if (ordersRes.status === 'fulfilled') {
        setOrders(ordersRes.value.data || []);
      } else {
        console.error('Error loading orders:', ordersRes.reason);
      }

      if (supportRes.status === 'fulfilled') {
        setSupportTickets(supportRes.value.data || []);
      }

      if (
        bookingsRes.status === 'rejected' &&
        ordersRes.status === 'rejected' &&
        supportRes.status === 'rejected'
      ) {
        toast.error('Failed to load dashboard');
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const persistCartItems = (items) => {
    setCartItems(items);
    localStorage.setItem(cartStorageKey, JSON.stringify(items));
  };

  const cartTotal = cartItems.reduce((total, item) => total + (Number(item.price || 0) * Number(item.quantity || 1)), 0);
  const cartItemCount = cartItems.reduce((total, item) => total + Number(item.quantity || 1), 0);

  const handleRemoveCartItem = (index) => {
    const nextItems = cartItems.filter((_, itemIndex) => itemIndex !== index);
    persistCartItems(nextItems);
    toast.success('Item removed from cart');
  };

  const handleClearCart = () => {
    persistCartItems([]);
    toast.success('Cart cleared');
  };

  const handleUpdateCartQuantity = (index, nextQuantity) => {
    if (nextQuantity < 1) {
      handleRemoveCartItem(index);
      return;
    }

    const nextItems = cartItems.map((item, itemIndex) => (
      itemIndex === index
        ? { ...item, quantity: nextQuantity }
        : item
    ));
    persistCartItems(nextItems);
  };

  const openHostedPayment = async (paymentData) => {
    const { url, order_id, payment_session_id, mode } = paymentData || {};

    if (!window.Cashfree || !payment_session_id) {
      if (url) {
        window.location.href = url;
        return;
      }
      throw new Error('Payment initialization failed');
    }

    const cashfreeMode = mode || process.env.REACT_APP_CASHFREE_MODE || 'sandbox';

    try {
      const cashfree = window.Cashfree({ mode: cashfreeMode });
      const result = await cashfree.checkout({
        paymentSessionId: payment_session_id,
        redirectTarget: '_modal'
      });

      const orderStatus = result?.order?.status || result?.status || result?.payment_status;
      const successOrderId = result?.order?.order_id || result?.orderId || order_id;
      const normalizedStatus = String(orderStatus || '').toUpperCase();

      if (
        normalizedStatus === 'PAID' ||
        normalizedStatus === 'SUCCESS' ||
        normalizedStatus === 'COMPLETED' ||
        result?.order?.payment_status === 'SUCCESS' ||
        result?.order?.payment_status === 'PAID'
      ) {
        toast.success('Payment successful!');
        navigate(`/payment-success?order_id=${encodeURIComponent(successOrderId)}`);
      } else if (
        normalizedStatus === 'FAILED' ||
        normalizedStatus === 'CANCELLED' ||
        normalizedStatus === 'FAILURE' ||
        result?.order?.payment_status === 'FAILED'
      ) {
        toast.error('Payment failed. Please try again.');
      } else {
        navigate(`/payment-success?order_id=${encodeURIComponent(successOrderId)}`);
      }
    } catch (cashfreeError) {
      console.error('Cashfree checkout error:', cashfreeError);
      if (order_id) {
        navigate(`/payment-success?order_id=${encodeURIComponent(order_id)}`);
      } else if (url) {
        toast.info('Opening payment page...');
        window.location.href = url;
      } else {
        throw new Error(`Cashfree payment failed: ${cashfreeError.message || 'Unknown error'}`);
      }
    }
  };

  const handleProceedToCheckout = async () => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    if (!shippingData.address.trim() || !shippingData.phone.trim()) {
      toast.error('Please fill all required shipping details');
      return;
    }

    try {
      setIsOpeningCartPayment(true);
      const token = localStorage.getItem('token');
      const payload = {
        items: cartItems.map((item) => ({
          product_id: item.id,
          quantity: Number(item.quantity || 1),
        })),
        origin_url: window.location.origin,
        shipping_address: shippingData.address.trim(),
        phone: shippingData.phone.trim(),
        notes: shippingData.notes.trim(),
      };

      const response = await axios.post(`${API_URL}/payments/cart-checkout`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShippingDialogOpen(false);
      await openHostedPayment(response.data);
    } catch (error) {
      console.error('Error creating cart checkout:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setIsOpeningCartPayment(false);
    }
  };

  const handlePayBooking = async (bookingId) => {
    try {
      setPayingItemKey(`booking-${bookingId}`);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/payments/checkout`,
        {
          booking_id: bookingId,
          origin_url: window.location.origin,
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      await openHostedPayment(response.data);
    } catch (error) {
      console.error('Error reopening booking payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to open booking payment');
    } finally {
      setPayingItemKey('');
    }
  };

  const handlePayOrder = async (productOrderId) => {
    try {
      setPayingItemKey(`order-${productOrderId}`);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/payments/order-checkout`,
        {
          product_order_id: productOrderId,
          origin_url: window.location.origin,
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      await openHostedPayment(response.data);
    } catch (error) {
      console.error('Error reopening order payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to open order payment');
    } finally {
      setPayingItemKey('');
    }
  };

  const handleRespondToTicket = async (ticketId) => {
    const message = supportReplies[ticketId] || '';
    if (!message.trim()) {
      toast.error('Please write a response');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/support-tickets/${ticketId}/respond`,
        { message },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Response sent');
      setSupportReplies((prev) => ({ ...prev, [ticketId]: '' }));
      fetchDashboardData();
    } catch (error) {
      console.error('Error responding to support ticket:', error);
      toast.error(error.response?.data?.detail || 'Failed to send response');
    }
  };

  const handleSubmitReview = async () => {
    if (!selectedBooking) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/reviews`,
        {
          booking_id: selectedBooking.id,
          designer_id: selectedBooking.designer_id,
          rating: reviewData.rating,
          comment: reviewData.comment
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Review submitted successfully!');
      setReviewDialogOpen(false);
      setReviewData({ rating: 5, comment: '' });
      fetchDashboardData();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  const handleSaveProfile = async () => {
    if (!profileData.name.trim() || !profileData.email.trim()) {
      toast.error('Please fill all profile fields');
      return;
    }

    try {
      setSavingProfile(true);
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/users/me`, profileData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const updatedUser = response.data?.user;
      if (updatedUser) {
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser?.(updatedUser);
      }
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      toast.error('Please fill all password fields');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    try {
      setSavingPassword(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/users/me/password`,
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password,
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      toast.success('Password updated successfully');
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setSavingPassword(false);
    }
  };

  const getBookingStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-green-400 bg-green-400/10';
      case 'pending': return 'text-yellow-400 bg-yellow-400/10';
      case 'completed': return 'text-blue-400 bg-blue-400/10';
      case 'cancelled': return 'text-red-400 bg-red-400/10';
      default: return 'text-gray-400 bg-gray-400/10';
    }
  };

  const getOrderStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-400 bg-green-400/10';
      case 'initiated': return 'text-yellow-400 bg-yellow-400/10';
      case 'failed': return 'text-red-400 bg-red-400/10';
      case 'expired': return 'text-gray-400 bg-gray-400/10';
      default: return 'text-gray-400 bg-gray-400/10';
    }
  };

  const getSupportStatusColor = (status) => {
    switch (status) {
      case 'resolved': return 'text-green-400 bg-green-400/10';
      case 'in_progress': return 'text-yellow-400 bg-yellow-400/10';
      case 'closed': return 'text-gray-300 bg-gray-500/10';
      case 'open': return 'text-blue-400 bg-blue-400/10';
      default: return 'text-gray-400 bg-gray-400/10';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar user={user} />

      <div className="max-w-7xl mx-auto px-4 py-24">
        <h1 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text" data-testid="dashboard-title">
          My Dashboard
        </h1>
        <p className="text-gray-400 mb-12 text-lg">
          Manage your profile, password, bookings, orders, cart, and support tickets here.
        </p>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#1a1a1a] border border-[#d4a574]/20 mb-8 flex flex-wrap h-auto">
            <TabsTrigger value="profile" className="data-[state=active]:bg-[#d4a574] data-[state=active]:text-[#0a0a0a]">
              <UserIcon className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="credentials" className="data-[state=active]:bg-[#d4a574] data-[state=active]:text-[#0a0a0a]">
              <Shield className="w-4 h-4 mr-2" />
              Password
            </TabsTrigger>
            <TabsTrigger value="bookings" className="data-[state=active]:bg-[#d4a574] data-[state=active]:text-[#0a0a0a]">
              <Calendar className="w-4 h-4 mr-2" />
              Bookings
            </TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-[#d4a574] data-[state=active]:text-[#0a0a0a]">
              <Package className="w-4 h-4 mr-2" />
              Orders
            </TabsTrigger>
            <TabsTrigger value="support" className="data-[state=active]:bg-[#d4a574] data-[state=active]:text-[#0a0a0a]">
              <LifeBuoy className="w-4 h-4 mr-2" />
              Support
            </TabsTrigger>
            <TabsTrigger value="cart" className="data-[state=active]:bg-[#d4a574] data-[state=active]:text-[#0a0a0a]">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Cart
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <div className="glass-card p-6 sm:p-8">
              <h2 className="text-2xl font-semibold text-[#f0d9b5] mb-6">Profile</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-gray-300 mb-2 block">Full Name</Label>
                  <Input
                    value={profileData.name}
                    onChange={(e) => setProfileData((prev) => ({ ...prev, name: e.target.value }))}
                    className="bg-[#0f0f0f] border-[#d4a574]/20 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 mb-2 block">Email</Label>
                  <Input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData((prev) => ({ ...prev, email: e.target.value }))}
                    className="bg-[#0f0f0f] border-[#d4a574]/20 text-white"
                  />
                </div>
              </div>
              <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
                <div className="text-sm text-gray-500">
                  Role: <span className="text-[#d4a574] font-medium">{user?.role || 'customer'}</span>
                </div>
                <button
                  onClick={handleSaveProfile}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={savingProfile}
                >
                  {savingProfile ? 'Saving...' : 'Save Profile'}
                </button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="credentials">
            <div className="glass-card p-6 sm:p-8">
              <h2 className="text-2xl font-semibold text-[#f0d9b5] mb-6">Credentials</h2>
              <div className="space-y-5 max-w-2xl">
                <div>
                  <Label className="text-gray-300 mb-2 block">Current Password</Label>
                  <Input
                    type="password"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData((prev) => ({ ...prev, current_password: e.target.value }))}
                    className="bg-[#0f0f0f] border-[#d4a574]/20 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 mb-2 block">New Password</Label>
                  <Input
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData((prev) => ({ ...prev, new_password: e.target.value }))}
                    className="bg-[#0f0f0f] border-[#d4a574]/20 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 mb-2 block">Confirm New Password</Label>
                  <Input
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData((prev) => ({ ...prev, confirm_password: e.target.value }))}
                    className="bg-[#0f0f0f] border-[#d4a574]/20 text-white"
                  />
                </div>
                <button
                  onClick={handleChangePassword}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={savingPassword}
                >
                  {savingPassword ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="bookings">
            {bookings.length === 0 ? (
              <div className="glass-card p-12 text-center" data-testid="no-bookings-message">
                <p className="text-gray-400 text-lg mb-6">You don't have any bookings yet.</p>
                <button onClick={() => navigate('/designers')} className="btn-primary" data-testid="browse-designers-btn">
                  Browse Designers
                </button>
              </div>
            ) : (
              <div className="space-y-6" data-testid="bookings-list">
                {bookings.map((booking) => (
                  <div key={booking.id} className="glass-card p-6" data-testid={`booking-card-${booking.id}`}>
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-4 flex-wrap">
                          <h3 className="text-2xl font-bold text-[#d4a574]">{booking.designer_name}</h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getBookingStatusColor(booking.status)}`}>
                            {booking.status}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-300">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-[#d4a574]" />
                            <span>{booking.booking_date}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-5 h-5 text-[#d4a574]" />
                            <span>{booking.time_slot}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-[#d4a574]" />
                            <span className="line-clamp-1">{booking.address}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[#d4a574] font-bold text-xl">{Number(booking.amount || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</span>
                          </div>
                        </div>

                        {booking.notes && (
                          <div className="mt-4 p-3 bg-[#1a1a1a] rounded-lg">
                            <p className="text-sm text-gray-400 whitespace-pre-line"><span className="font-semibold">Notes:</span> {booking.notes}</p>
                          </div>
                        )}
                      </div>

                      {booking.status === 'confirmed' ? (
                        <div>
                          <Dialog open={reviewDialogOpen && selectedBooking?.id === booking.id} onOpenChange={(open) => {
                            setReviewDialogOpen(open);
                            if (open) setSelectedBooking(booking);
                          }}>
                            <DialogTrigger asChild>
                              <button className="btn-secondary flex items-center gap-2" data-testid={`add-review-btn-${booking.id}`}>
                                <MessageSquare className="w-5 h-5" />
                                Add Review
                              </button>
                            </DialogTrigger>
                            <DialogContent className="bg-[#1a1a1a] text-white border-[#d4a574]/20">
                              <DialogHeader>
                                <DialogTitle className="text-2xl font-bold gradient-text">Rate Your Experience</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-6 mt-4">
                                <div>
                                  <Label className="text-gray-300 mb-3 block">Rating</Label>
                                  <div className="flex gap-2">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <button
                                        key={star}
                                        onClick={() => setReviewData({ ...reviewData, rating: star })}
                                        className="transition-transform hover:scale-110"
                                        data-testid={`rating-star-${star}`}
                                      >
                                        <Star
                                          className={`w-8 h-8 ${star <= reviewData.rating ? 'text-[#d4a574] fill-current' : 'text-gray-600'}`}
                                        />
                                      </button>
                                    ))}
                                  </div>
                                </div>
                                <div>
                                  <Label htmlFor="comment" className="text-gray-300 mb-2 block">Your Review</Label>
                                  <Textarea
                                    id="comment"
                                    placeholder="Share your experience..."
                                    value={reviewData.comment}
                                    onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
                                    className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white focus:outline-none focus:border-[#d4a574]"
                                    rows={4}
                                    data-testid="review-comment-input"
                                  />
                                </div>
                                <button onClick={handleSubmitReview} className="btn-primary w-full" data-testid="submit-review-btn">
                                  Submit Review
                                </button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      ) : booking.status === 'pending' ? (
                        <button
                          onClick={() => handlePayBooking(booking.id)}
                          className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={payingItemKey === `booking-${booking.id}`}
                        >
                          {payingItemKey === `booking-${booking.id}` ? <Loader className="w-4 h-4 animate-spin" /> : null}
                          Pay Now
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="orders">
            {orders.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <p className="text-gray-400 text-lg mb-6">You don’t have any product orders yet.</p>
                <button onClick={() => navigate('/products')} className="btn-primary">
                  Browse Products
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.id} className="glass-card p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                      <div>
                        <p className="text-sm text-gray-400 mb-1">Order ID</p>
                        <p className="font-semibold break-all">{order.session_id}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400 mb-1">{order.order_type === 'product' ? 'Summary' : 'Designer'}</p>
                        <p className="font-semibold">{order.order_type === 'product' ? (order.item_summary || 'Wearoclub Order') : (order.designer_name || 'Wearoclub Designer')}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400 mb-1">{order.order_type === 'product' ? 'Items' : 'Booking Date'}</p>
                        <p className="font-semibold">{order.order_type === 'product' ? `${order.item_count || 0} item${order.item_count === 1 ? '' : 's'}` : (order.booking_date || 'Not available')}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400 mb-1">Amount</p>
                        <p className="font-bold text-[#d4a574]">{Number(order.amount || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium w-fit ${getOrderStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                        <span className="text-sm text-gray-400">Payment: {order.payment_status}</span>
                        {order.payment_status !== 'paid' && order.product_order_id ? (
                          <button
                            onClick={() => handlePayOrder(order.product_order_id)}
                            className="btn-primary mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={payingItemKey === `order-${order.product_order_id}`}
                          >
                            {payingItemKey === `order-${order.product_order_id}` ? 'Opening...' : 'Pay Now'}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="support">
            <div className="flex justify-end mb-6">
              <button onClick={() => navigate('/support')} className="btn-primary">
                Create Support Ticket
              </button>
            </div>
            {supportTickets.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <p className="text-gray-400 text-lg mb-6">You haven’t created any support tickets yet.</p>
                <button onClick={() => navigate('/support')} className="btn-primary">
                  Contact Support
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {supportTickets.map((ticket) => (
                  <div key={ticket.id} className="glass-card p-6">
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-400 mb-1">{ticket.ticket_number}</p>
                        <h3 className="text-xl font-bold text-[#f0d9b5]">{ticket.subject}</h3>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSupportStatusColor(ticket.status)}`}>
                        {ticket.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 text-sm">
                      <div>
                        <p className="text-gray-400 mb-1">Category</p>
                        <p className="font-semibold">{ticket.category}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 mb-1">Created</p>
                        <p className="font-semibold">{new Date(ticket.created_at).toLocaleString('en-IN')}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 mb-1">Latest Update</p>
                        <p className="font-semibold">{new Date(ticket.updated_at).toLocaleString('en-IN')}</p>
                      </div>
                    </div>
                    <div className="mb-4">
                      <p className="text-gray-400 mb-2 text-sm">Message</p>
                      <p className="text-gray-300 whitespace-pre-line">{ticket.message}</p>
                    </div>
                    <div className="mb-5">
                      <p className="text-gray-400 mb-2 text-sm">Response History</p>
                      {ticket.responses && ticket.responses.length > 0 ? (
                        <div className="space-y-3">
                          {ticket.responses.map((note, index) => (
                            <div key={`${ticket.id}-${index}`} className="rounded-lg border border-[#d4a574]/10 bg-[#0f0f0f] p-4">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                                <div>
                                  <p className="font-semibold text-[#f0d9b5]">
                                    {note.sender_name} {note.sender_role === 'admin' ? '(Team)' : '(You)'}
                                  </p>
                                  <p className="text-sm text-gray-500">{note.sender_email}</p>
                                </div>
                                <p className="text-sm text-gray-500">{new Date(note.created_at).toLocaleString('en-IN')}</p>
                              </div>
                              <p className="text-gray-300 whitespace-pre-line">{note.message}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500">No team responses yet, Our team will respond soon, Thank You for your patience.</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-gray-300 mb-2 block">Respond</Label>
                      <Textarea
                        value={supportReplies[ticket.id] || ''}
                        onChange={(e) => setSupportReplies((prev) => ({ ...prev, [ticket.id]: e.target.value }))}
                        className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white"
                        rows={4}
                        placeholder="Write your response..."
                      />
                      <div className="mt-3 flex justify-end">
                        <button onClick={() => handleRespondToTicket(ticket.id)} className="btn-primary">
                          Respond
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="cart">
            {cartItems.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <p className="text-gray-400 text-lg mb-3">Your cart is empty.</p>
                <p className="text-gray-500 mb-6">Products you save for later will appear here for this account only on this device.</p>
                <button onClick={() => navigate('/products')} className="btn-primary">
                  Browse Products
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="glass-card p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                    <div>
                      <p className="text-sm uppercase tracking-[0.2em] text-gray-500 mb-2">Cart Summary</p>
                      <h3 className="text-3xl font-bold text-[#f0d9b5]">
                        {cartTotal.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
                      </h3>
                      <p className="text-gray-400 mt-2">
                        {cartItemCount} item{cartItemCount > 1 ? 's' : ''} ready for checkout.
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button onClick={handleClearCart} className="btn-secondary flex items-center justify-center gap-2">
                        <Trash2 className="w-4 h-4" />
                        Clear Cart
                      </button>
                      <Dialog open={shippingDialogOpen} onOpenChange={setShippingDialogOpen}>
                        <DialogTrigger asChild>
                          <button className="btn-primary flex items-center justify-center gap-2">
                            Proceed to Checkout
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        </DialogTrigger>
                        <DialogContent className="bg-[#1a1a1a] text-white border-[#d4a574]/20 max-w-2xl max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle className="text-2xl font-bold gradient-text">Checkout Details</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-5 mt-4">
                            <div className="glass-card p-4">
                              <p className="text-sm uppercase tracking-[0.18em] text-gray-500 mb-2">Order Summary</p>
                              <p className="text-2xl font-bold text-[#f0d9b5] mb-1">
                                {cartTotal.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
                              </p>
                              <p className="text-gray-400">
                                {cartItemCount} item{cartItemCount > 1 ? 's' : ''} in this order.
                              </p>
                            </div>
                            <div>
                              <Label htmlFor="shipping-address" className="text-gray-300 mb-2 block">Shipping Address *</Label>
                              <Textarea
                                id="shipping-address"
                                placeholder="Enter your full shipping address"
                                value={shippingData.address}
                                onChange={(e) => setShippingData((prev) => ({ ...prev, address: e.target.value }))}
                                className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white"
                                rows={4}
                              />
                            </div>
                            <div>
                              <Label htmlFor="shipping-phone" className="text-gray-300 mb-2 block">Phone Number *</Label>
                              <Input
                                id="shipping-phone"
                                type="tel"
                                placeholder="Enter your phone number"
                                value={shippingData.phone}
                                onChange={(e) => setShippingData((prev) => ({ ...prev, phone: e.target.value }))}
                                className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white"
                              />
                            </div>
                            <div>
                              <Label htmlFor="shipping-notes" className="text-gray-300 mb-2 block">Delivery Notes</Label>
                              <Textarea
                                id="shipping-notes"
                                placeholder="Add landmark or delivery instructions"
                                value={shippingData.notes}
                                onChange={(e) => setShippingData((prev) => ({ ...prev, notes: e.target.value }))}
                                className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white"
                                rows={3}
                              />
                            </div>
                            <button
                              onClick={handleProceedToCheckout}
                              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={isOpeningCartPayment}
                            >
                              {isOpeningCartPayment ? 'Opening Payment...' : 'Pay Now'}
                            </button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-4">
                    Proceed to Checkout, add shipping details, then pay securely.
                  </p>
                </div>
                {cartItems.map((item, index) => (
                  <div key={`${item.id || item.name}-${index}`} className="glass-card p-6">
                    <div className="flex flex-col md:flex-row gap-5 md:items-center">
                      <img
                        src={item.image || item.images?.[0] || 'https://via.placeholder.com/120x160?text=Product'}
                        alt={item.name || 'Cart item'}
                        className="w-28 h-36 rounded-lg object-cover"
                      />
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-[#f0d9b5] mb-2">{item.name || 'Wearoclub Product'}</h3>
                        <p className="text-[#d4a574] font-bold text-lg mb-2">
                          {Number(item.price || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
                        </p>
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-sm text-gray-400">Quantity</span>
                          <div className="inline-flex items-center rounded-lg border border-[#d4a574]/20 bg-[#0f0f0f]">
                            <button
                              type="button"
                              onClick={() => handleUpdateCartQuantity(index, Number(item.quantity || 1) - 1)}
                              className="px-3 py-2 text-gray-300 hover:text-[#d4a574]"
                              aria-label="Decrease quantity"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="min-w-10 px-3 py-2 text-center font-semibold text-white">
                              {Number(item.quantity || 1)}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleUpdateCartQuantity(index, Number(item.quantity || 1) + 1)}
                              className="px-3 py-2 text-gray-300 hover:text-[#d4a574]"
                              aria-label="Increase quantity"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                          <span className="text-sm text-gray-400">
                            Total: {(Number(item.price || 0) * Number(item.quantity || 1)).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
                          </span>
                        </div>
                        {item.description && (
                          <p className="text-gray-400 text-sm line-clamp-2">{item.description}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveCartItem(index)}
                        className="px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors flex items-center justify-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default UserDashboard;
